import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@shared/services/api';
import { Card, Button, Input } from '@shared/components/ui';
import { Mail, ShieldCheck, Send, CheckCircle2, XCircle, Lock } from 'lucide-react';

interface SMTPConfig {
  id?: string;
  smtp_host: string;
  smtp_port: number;
  smtp_username: string;
  from_email: string;
  from_name: string;
  use_tls: boolean;
  is_enabled: boolean;
}

export default function SMTPSettings() {
  const { t } = useTranslation('settings');
  const [settings, setSettings] = useState<SMTPConfig>({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    from_email: '',
    from_name: '',
    use_tls: true,
    is_enabled: false,
  });
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setLoadFailed(false);
      const res = await api.get('/smtp-settings');
      setSettings(res.data);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || loadFailed) return;
    setSaving(true);
    setMessage(null);
    try {
      const payload = { ...settings, ...(password ? { smtp_password: password } : {}) };
      await api.put('/smtp-settings', payload);
      setMessage({ type: 'success', text: t('smtp.messages.saved') });
      setPassword('');
    } catch {
      setMessage({ type: 'error', text: t('smtp.errors.saveFailed') });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (loading || loadFailed || !settings.smtp_host) return;
    setTesting(true);
    setMessage(null);
    try {
      const url = testEmail
        ? `/smtp-settings/test?test_email=${encodeURIComponent(testEmail)}`
        : '/smtp-settings/test';
      await api.post(url);
      setMessage({ type: 'success', text: t('smtp.messages.testSent') });
    } catch {
      setMessage({ type: 'error', text: t('smtp.errors.testFailed') });
    } finally {
      setTesting(false);
    }
  };

  return (
    <>
      <div className="max-w-6xl mx-auto space-y-6 animate-in fade-in duration-300">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary-light dark:text-text-primary-dark tracking-tight">
            {t('smtp.title')}
          </h1>
          <p className="text-text-muted-light dark:text-text-muted-dark mt-1">
            {t('smtp.subtitle')}
          </p>
        </div>

        {message && (
          <div
            role={message.type === 'error' ? 'alert' : 'status'}
            aria-live="polite"
            className={`p-4 rounded-xl border font-bold flex items-center gap-3 ${
              message.type === 'success'
                ? 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark border-state-ok-light/20 dark:border-state-ok-dark/20 text-state-ok-light dark:text-state-ok-dark'
                : 'bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-state-danger-light/20 dark:border-state-danger-dark/20 text-state-danger-light dark:text-state-danger-dark'
            }`}
          >
            {message.type === 'success' ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
            {message.text}
          </div>
        )}

        {loading ? (
          <div className="py-16 flex justify-center" role="status" aria-live="polite">
            <span className="sr-only">{t('smtp.loading')}</span>
            <div
              className="animate-spin h-10 w-10 border-b-2 border-brand-600 rounded-full"
              aria-hidden="true"
            ></div>
          </div>
        ) : loadFailed ? (
          <div
            role="alert"
            className="flex flex-col gap-4 rounded-xl border border-state-danger-light/30 bg-state-danger-bg-light p-5 text-state-danger-light dark:border-state-danger-dark/30 dark:bg-state-danger-bg-dark dark:text-state-danger-dark sm:flex-row sm:items-center sm:justify-between"
          >
            <p>{t('smtp.errors.loadFailed')}</p>
            <Button type="button" variant="secondary" onClick={fetchSettings}>
              {t('smtp.retry')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Card className="p-5 sm:p-6">
                <form onSubmit={handleSave} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Input
                      id="smtp-host"
                      label={t('smtp.host')}
                      value={settings.smtp_host}
                      onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                      required
                      placeholder={t('smtp.placeholders.host')}
                    />
                    <Input
                      id="smtp-port"
                      label={t('smtp.port')}
                      type="number"
                      value={settings.smtp_port}
                      onChange={(e) =>
                        setSettings({ ...settings, smtp_port: parseInt(e.target.value) || 587 })
                      }
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Input
                      id="smtp-username"
                      label={t('smtp.username')}
                      value={settings.smtp_username}
                      onChange={(e) => setSettings({ ...settings, smtp_username: e.target.value })}
                      placeholder={t('smtp.placeholders.username')}
                    />
                    <div>
                      <label htmlFor="smtp-password" className="app-field-label">
                        {t('smtp.password')}
                      </label>
                      <div className="relative">
                        <Lock
                          size={16}
                          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-subtle-light dark:text-text-subtle-dark"
                        />
                        <input
                          id="smtp-password"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          autoComplete="new-password"
                          placeholder={t('smtp.placeholders.password')}
                          className="app-field-control w-full pl-10 pr-4"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Input
                      id="smtp-from-address"
                      label={t('smtp.fromAddress')}
                      type="email"
                      value={settings.from_email}
                      onChange={(e) => setSettings({ ...settings, from_email: e.target.value })}
                      required
                      placeholder={t('smtp.placeholders.fromAddress')}
                    />
                    <Input
                      id="smtp-sender-name"
                      label={t('smtp.senderName')}
                      value={settings.from_name}
                      onChange={(e) => setSettings({ ...settings, from_name: e.target.value })}
                      placeholder={t('smtp.placeholders.senderName')}
                    />
                  </div>

                  <div className="pt-5 border-t border-surface-border-light dark:border-surface-border-dark flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
                    <div className="flex flex-wrap gap-x-6 gap-y-2">
                      <label className="min-h-11 flex items-center gap-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={settings.use_tls}
                          onChange={(e) => setSettings({ ...settings, use_tls: e.target.checked })}
                          className="h-5 w-5 rounded border-border-strong-light dark:border-border-strong-dark text-brand-600 dark:text-brand-400"
                        />
                        <span className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                          {t('smtp.useTLS')}
                        </span>
                      </label>
                      <label className="min-h-11 flex items-center gap-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={settings.is_enabled}
                          onChange={(e) =>
                            setSettings({ ...settings, is_enabled: e.target.checked })
                          }
                          className="h-5 w-5 rounded border-border-strong-light dark:border-border-strong-dark text-brand-600 dark:text-brand-400"
                        />
                        <span className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                          {t('smtp.enabled')}
                        </span>
                      </label>
                    </div>
                    <Button type="submit" isLoading={saving}>
                      {saving ? t('smtp.saving') : t('smtp.save')}
                    </Button>
                  </div>
                </form>
              </Card>

              <Card className="p-5 sm:p-6 bg-surface-2-light dark:bg-surface-2-dark border-dashed border-2">
                <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark mb-2 flex items-center gap-2">
                  <Send className="text-state-info-light dark:text-state-info-dark" size={18} />{' '}
                  {t('smtp.deliveryTest.title')}
                </h2>
                <p className="text-sm text-text-muted-light dark:text-text-muted-dark mb-5">
                  {t('smtp.deliveryTest.description')}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                  <Input
                    id="smtp-test-recipient"
                    label={t('smtp.deliveryTest.recipient')}
                    type="email"
                    placeholder={t('smtp.deliveryTest.recipientPlaceholder')}
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="flex-1 bg-surface-card-light dark:bg-surface-card-dark"
                  />
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={handleTest}
                    isLoading={testing}
                    disabled={!settings.smtp_host}
                    className="min-w-[116px] whitespace-nowrap"
                  >
                    {testing ? t('smtp.deliveryTest.sending') : t('smtp.deliveryTest.send')}
                  </Button>
                </div>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="p-6 bg-surface-2-light dark:bg-surface-2-dark relative overflow-hidden">
                <Mail
                  size={120}
                  className="absolute -bottom-8 -right-8 text-brand-600 dark:text-brand-400 opacity-10 rotate-12"
                  aria-hidden="true"
                />
                <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark mb-3 relative z-10">
                  {t('smtp.guide.title')}
                </h2>
                <p className="text-sm text-text-muted-light dark:text-text-muted-dark leading-relaxed relative z-10">
                  {t('smtp.guide.description')}
                </p>
              </Card>

              <Card className="p-6 border-l-4 border-l-state-warn-light dark:border-l-state-warn-dark">
                <h2 className="text-sm font-semibold text-text-primary-light dark:text-text-primary-dark mb-2 flex items-center gap-2">
                  <ShieldCheck
                    size={14}
                    className="text-state-warn-light dark:text-state-warn-dark"
                  />{' '}
                  {t('smtp.security.title')}
                </h2>
                <p className="text-xs text-text-muted-light dark:text-text-muted-dark font-medium leading-relaxed">
                  {t('smtp.security.description')}
                </p>
              </Card>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
